import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { initials } from '../components/community/Avatar';
import { AppFrame } from '../components/frame/AppFrame';
import { GameCover, GameTile } from '../components/games/GameArt';
import { Alert, buttonClass } from '../components/ui';
import { fetchHome, type HomeData } from '../lib/games';
import { useSession } from '../lib/session';

/**
 * mogzo.com's home feed, games part (M29): the three games you played last ("Jump back in"),
 * which shrink into the header once scrolled past, and games you might like. Posts in the
 * feed come later (Phase 2).
 */
export function Feed() {
  const { me } = useSession();
  const [home, setHome] = useState<HomeData | null>(null);
  const [failed, setFailed] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const recentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let active = true;
    fetchHome()
      .then((data) => active && setHome(data))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, []);

  const recent = home?.recent ?? [];
  // biome-ignore lint/correctness/useExhaustiveDependencies: watches the section once it's drawn
  useEffect(() => {
    const section = recentRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setScrolled(!entry?.isIntersecting), {
      rootMargin: '-56px 0px 0px 0px',
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, [recent.length]);

  const shrunk =
    scrolled && recent.length ? (
      <nav aria-label="Jump back in" className="flex pr-1">
        {recent.map((game, i) => (
          <Link
            key={game.id}
            to={game.path}
            aria-label={`Open ${game.name}`}
            className={`overflow-hidden rounded-full border-2 border-card ${i ? '-ml-1.5' : ''}`}
          >
            {game.iconUrl ? (
              <img src={game.iconUrl} alt="" className="size-[34px] object-cover" />
            ) : (
              <span className="flex size-[34px] items-center justify-center bg-brand text-[11px] font-bold">
                {initials(game.name)}
              </span>
            )}
          </Link>
        ))}
      </nav>
    ) : null;

  return (
    <AppFrame headerEnd={shrunk}>
      <main aria-label="Feed" className="mx-auto w-full max-w-[760px] px-3 pb-10 lg:px-6 lg:pt-6">
        {recent.length > 0 && (
          <section
            ref={recentRef}
            aria-labelledby="jump-back-in"
            className="-mx-3 border-b border-line bg-card px-4 pt-1 pb-3.5 lg:mx-0 lg:rounded-[18px] lg:border lg:p-4"
          >
            <h2
              id="jump-back-in"
              className="text-xs font-semibold tracking-[0.06em] text-muted uppercase"
            >
              Jump back in
            </h2>
            <ul className="mt-2 grid grid-cols-3 gap-2.5">
              {recent.map((game) => (
                <li key={game.id} className="min-w-0">
                  <Link to={game.path} className="group block">
                    <GameCover game={game} className="h-[70px] rounded-xl lg:h-[110px]" />
                    <span className="mt-1.5 block truncate text-[13px] font-semibold group-hover:underline">
                      {game.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="flex flex-col gap-5 pt-4">
          {me === null && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-line bg-card px-4 py-3.5">
              <span className="text-sm leading-relaxed">
                Sign in to play, join game communities and keep your games together.
              </span>
              <Link to="/sign-in" className={buttonClass('primary', false)}>
                Sign in
              </Link>
            </div>
          )}
          {failed && <Alert>Couldn’t load the feed. Reload the page to try again.</Alert>}
          {!home && !failed && (
            <p role="status" className="py-8 text-center text-sm text-muted">
              Loading…
            </p>
          )}
          {home && home.suggested.length > 0 && (
            <section aria-labelledby="you-might-like">
              <div className="flex items-baseline justify-between gap-3 px-1">
                <h2 id="you-might-like" className="text-lg leading-6 font-bold tracking-[-0.01em]">
                  Games you might like
                </h2>
                <Link
                  to="/discover"
                  className="text-[13px] font-semibold text-muted hover:text-ink"
                >
                  See all
                </Link>
              </div>
              <ul className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-3">
                {home.suggested.map((game) => (
                  <li key={game.id}>
                    <GameTile game={game} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          {home && !home.recent.length && !home.suggested.length && (
            <p className="rounded-[18px] border border-dashed border-line px-5 py-8 text-center text-muted">
              No games yet. They show here once they’re live.
            </p>
          )}
        </div>
      </main>
    </AppFrame>
  );
}
